import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toolkitApi } from "@/api/toolkit";
import type {
  InsurerContactPayload,
  OwuUploadDetails,
  ToolkitDocumentDetails,
} from "@/types/toolkit";

export const toolkitKeys = {
  all: ["toolkit"] as const,
  contacts: () => [...toolkitKeys.all, "contacts"] as const,
  documents: () => [...toolkitKeys.all, "documents"] as const,
  owu: () => [...toolkitKeys.all, "owu"] as const,
  owuDocuments: (includeArchived: boolean) =>
    [...toolkitKeys.owu(), "documents", includeArchived] as const,
  owuStatus: () => [...toolkitKeys.owu(), "status"] as const,
  owuSources: () => [...toolkitKeys.owu(), "sources"] as const,
  searchStatus: () => [...toolkitKeys.all, "search"] as const,
};

export function useInsurerContacts() {
  return useQuery({
    queryKey: toolkitKeys.contacts(),
    queryFn: () => toolkitApi.getContacts(),
    staleTime: 60 * 1000,
  });
}

export function useToolkitDocuments() {
  return useQuery({
    queryKey: toolkitKeys.documents(),
    queryFn: () => toolkitApi.getDocuments(),
    staleTime: 60 * 1000,
  });
}

function useInvalidating<TVariables, TResult>(
  mutationFn: (variables: TVariables) => Promise<TResult>,
  queryKey: readonly unknown[],
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}

/** `id` null creates a new contact for `insurerId`. */
export function useSaveContact() {
  return useInvalidating(
    ({
      id,
      insurerId,
      payload,
    }: {
      id: string | null;
      insurerId: string;
      payload: InsurerContactPayload;
    }) =>
      id === null
        ? toolkitApi.createContact(insurerId, payload)
        : toolkitApi.updateContact(id, payload),
    toolkitKeys.contacts(),
  );
}

export function useDeleteContact() {
  return useInvalidating(
    (id: string) => toolkitApi.deleteContact(id),
    toolkitKeys.contacts(),
  );
}

export function useUploadDocument() {
  return useInvalidating(
    ({ details, file }: { details: ToolkitDocumentDetails; file: File }) =>
      toolkitApi.uploadDocument(details, file),
    toolkitKeys.documents(),
  );
}

/** Saves the details and, when a file is given, uploads it as a new version. */
export function useUpdateDocument() {
  return useInvalidating(
    async ({
      id,
      details,
      file,
    }: {
      id: string;
      details: ToolkitDocumentDetails;
      file: File | null;
    }) => {
      const updated = await toolkitApi.updateDocument(id, details);
      return file ? toolkitApi.replaceDocumentFile(id, file) : updated;
    },
    toolkitKeys.documents(),
  );
}

export function useDeleteDocument() {
  return useInvalidating(
    (id: string) => toolkitApi.deleteDocument(id),
    toolkitKeys.documents(),
  );
}

export function useOwuDocuments(includeArchived: boolean) {
  return useQuery({
    queryKey: toolkitKeys.owuDocuments(includeArchived),
    queryFn: () => toolkitApi.getOwuDocuments(includeArchived),
    staleTime: 60 * 1000,
  });
}

/** Polls while a check is running, so new OWU show up when it finishes. */
export function useOwuStatus() {
  return useQuery({
    queryKey: toolkitKeys.owuStatus(),
    queryFn: () => toolkitApi.getOwuStatus(),
    refetchInterval: (query) =>
      query.state.data?.lastRun && !query.state.data.lastRun.finishedAt
        ? 10 * 1000
        : false,
  });
}

export function useOwuSources(enabled: boolean) {
  return useQuery({
    queryKey: toolkitKeys.owuSources(),
    queryFn: () => toolkitApi.getOwuSources(),
    enabled,
  });
}

export function useUploadOwu() {
  return useInvalidating(
    ({ details, file }: { details: OwuUploadDetails; file: File }) =>
      toolkitApi.uploadOwu(details, file),
    toolkitKeys.owu(),
  );
}

export function useDeleteOwu() {
  return useInvalidating(
    (id: string) => toolkitApi.deleteOwu(id),
    toolkitKeys.owu(),
  );
}

export function useStartOwuScan() {
  return useInvalidating(() => toolkitApi.startOwuScan(), toolkitKeys.owu());
}

export function useAddOwuSource() {
  return useInvalidating(
    ({
      insurerId,
      domain,
      startUrl,
    }: {
      insurerId: string;
      domain: string;
      startUrl: string;
    }) => toolkitApi.addOwuSource(insurerId, domain, startUrl),
    toolkitKeys.owuSources(),
  );
}

export function useDeleteOwuSource() {
  return useInvalidating(
    (id: string) => toolkitApi.deleteOwuSource(id),
    toolkitKeys.owuSources(),
  );
}

export function useSearchStatus() {
  return useQuery({
    queryKey: toolkitKeys.searchStatus(),
    queryFn: () => toolkitApi.getSearchStatus(),
  });
}

export function useKnowledgeSearch() {
  return useMutation({
    mutationFn: (question: string) => toolkitApi.search(question),
  });
}
